/**
 * API contract types.
 *
 * Mirrors the backend's domain and design types. Kept hand-written rather than
 * generated so the UI depends only on the fields it actually renders.
 */

export type AnalysisStatus =
  | 'queued'
  | 'crawling'
  | 'extracting'
  | 'analyzing'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type Viewport = 'desktop' | 'tablet' | 'mobile';
export type CrawlMode = 'single' | 'website' | 'selected';
export type AnalysisDepth = 'quick' | 'standard' | 'deep';
export type PageStatus = 'pending' | 'crawled' | 'failed' | 'skipped';

export const TERMINAL_STATUSES: AnalysisStatus[] = ['completed', 'failed', 'cancelled'];

export interface AnalysisOptions {
  crawlMode: CrawlMode;
  maxPages: number;
  viewports: Viewport[];
  depth: AnalysisDepth;
  visualAnalysis: boolean;
  cssAnalysis: boolean;
  componentAnalysis: boolean;
  responsiveAnalysis: boolean;
  pages?: string[];
  forceRefresh?: boolean;
}

export interface Analysis {
  id: string;
  projectId: string | null;
  url: string;
  normalizedUrl: string;
  status: AnalysisStatus;
  progress: number;
  currentStep: string | null;
  errorMessage: string | null;
  errorCode: string | null;
  options: AnalysisOptions;
  retryCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  domain: string;
  favicon: string;
  pageCount: number;
  screenshotCount: number;
  hasDocument: boolean;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateAnalysisResult {
  analysis: Analysis;
  cachedFrom: string | null;
  quota: { used: number; limit: number; resetAt: string };
}

export interface ProgressStep {
  key: string;
  label: string;
}

export interface AnalysisProgress {
  analysisId: string;
  status: AnalysisStatus;
  progress: number;
  currentStep: string | null;
  message: string | null;
  steps: ProgressStep[];
  errorCode: string | null;
  errorMessage: string | null;
  updatedAt: string;
}

export interface Project {
  id: string;
  userId: string | null;
  name: string;
  description: string | null;
  analysisCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AnalysisPage {
  id: string;
  analysisId: string;
  url: string;
  title: string | null;
  depth: number;
  status: PageStatus;
  createdAt: string;
}

export interface ScreenshotView {
  id: string;
  pageUrl: string;
  viewport: Viewport;
  width: number;
  height: number;
  bytes: number;
  url: string;
}

// ---------------------------------------------------------------------------
// Design system
// ---------------------------------------------------------------------------

export interface ColorToken {
  hex: string;
  rgb: string;
  hsl: string;
  count: number;
  usage: Array<'text' | 'background' | 'border'>;
  weight: number;
}

export interface ContrastCheck {
  label: string;
  foreground: string;
  background: string;
  ratio: number;
  passesAA: boolean;
  passesAAA: boolean;
}

export interface ColorSystem {
  palette: ColorToken[];
  roles: Record<string, string | undefined>;
  scheme: 'light' | 'dark';
  contrast: ContrastCheck[];
}

export interface FontFamilyToken {
  family: string;
  stack: string;
  count: number;
  isSystem: boolean;
  roles: Array<'heading' | 'body' | 'mono'>;
}

export interface TypeScaleStep {
  name: string;
  fontSize: number;
  fontSizeRem: number;
  fontWeight: number;
  lineHeight: number | null;
  lineHeightRatio: number | null;
  letterSpacing: string | null;
  family: string;
  sources: string[];
  count: number;
}

export interface TypographySystem {
  families: FontFamilyToken[];
  headingFamily: string | null;
  bodyFamily: string | null;
  monoFamily: string | null;
  scale: TypeScaleStep[];
  weights: number[];
  baseFontSize: number;
  webFonts: string[];
}

export interface SpacingSystem {
  baseUnit: number;
  scale: number[];
  tokens: Array<{ name: string; value: number; count: number }>;
  containerPadding: number | null;
  containerMaxWidth: number | null;
  sectionSpacing: number | null;
  gaps: number[];
}

export interface RadiusSystem {
  tokens: Array<{ name: string; value: number; count: number; usedBy: string[] }>;
  card: number | null;
  button: number | null;
  input: number | null;
  pill: boolean;
}

export interface ShadowSystem {
  tokens: Array<{
    name: string;
    value: string;
    elevation: number;
    count: number;
    inset: boolean;
    usedBy: string[];
  }>;
  prefersBorders: boolean;
  borderWidths: number[];
  borderColors: string[];
}

export interface LayoutSystem {
  containerMaxWidth: number | null;
  containerPadding: number | null;
  usesGrid: boolean;
  usesFlex: boolean;
  gridPatterns: Array<{ pattern: string; columns: number; count: number }>;
  headerHeight: number | null;
  footerHeight: number | null;
  landmarks: string[];
}

export interface ComponentVariant {
  name: string;
  backgroundColor: string | null;
  color: string | null;
  borderColor: string | null;
  borderWidth: number | null;
  borderRadius: number | null;
  boxShadow: string | null;
  fontSize: number | null;
  fontWeight: number | null;
  paddingX: number | null;
  paddingY: number | null;
  height: number | null;
  count: number;
}

export interface DetectedComponent {
  kind: string;
  name: string;
  count: number;
  confidence: number;
  description: string;
  variants: ComponentVariant[];
  metrics: {
    avgWidth: number;
    avgHeight: number;
    paddingX: number | null;
    paddingY: number | null;
    gap: number | null;
  };
  viewports: Viewport[];
  responsiveNotes: string[];
  states: string[];
}

export interface ResponsiveSystem {
  viewports: Viewport[];
  breakpoints: number[];
  observations: Array<{
    category: string;
    summary: string;
    desktop: string | null;
    tablet: string | null;
    mobile: string | null;
  }>;
  typographyScaling: Array<{
    step: string;
    desktop: number | null;
    tablet: number | null;
    mobile: number | null;
  }>;
}

export interface VisionInsights {
  provider: string;
  model: string;
  mocked: boolean;
  aesthetic: string;
  designDirection: string;
  visualHierarchy: string;
  density: string;
  typographyPersonality: string;
  colorRelationships: string;
  spacingRhythm: string;
  componentNotes: string[];
  responsiveNotes: string[];
  keywords: string[];
}

export interface AnalysisScore {
  total: number;
  formula: string;
  breakdown: Array<{ key: string; label: string; score: number; max: number; detail: string }>;
}

export interface AnalysisSummary {
  url: string;
  domain: string;
  pagesAnalyzed: number;
  screenshots: number;
  colorCount: number;
  fontCount: number;
  componentCount: number;
  score: AnalysisScore;
}

export interface StylesResponse {
  analysisId: string;
  colors: ColorSystem;
  typography: TypographySystem;
  spacing: SpacingSystem;
  radius: RadiusSystem;
  shadows: ShadowSystem;
  layout: LayoutSystem;
  responsive: ResponsiveSystem;
  raw: {
    pagesAnalyzed: number;
    elementsAnalyzed: number;
    elementsTruncated: boolean;
    cssVariables: Record<string, string>;
    webFonts: string[];
    themeColor: string | null;
    declaredColorScheme: string | null;
  };
  vision: VisionInsights | null;
  summary: AnalysisSummary | null;
}

export interface DesignDocument {
  id: string;
  analysisId: string;
  content: string;
  version: number;
  isGenerated: boolean;
  createdAt: string;
  updatedAt: string;
  versions: Array<{ version: number; isGenerated: boolean; createdAt: string }>;
}

export type ExportFormat =
  'design-md' | 'design-json' | 'tokens-json' | 'css-variables' | 'tailwind-config' | 'ai-prompt';

/**
 * `storage`, `queue`, `auth`, `scoreFormula` and the `configured` flags are
 * infrastructure detail the API only includes for admins (see
 * PublicConfig in backend/src/config/env.ts) - check `session.isAdmin`
 * before relying on them.
 */
export interface AppSettings {
  database: { provider: string; configured?: boolean };
  storage?: { provider: string; configured: boolean };
  ai: { provider: string; model: string | null; mocked: boolean; configured?: boolean };
  queue?: { provider: string };
  auth?: { provider: string; enabled: boolean };
  limits: {
    maxPages: number;
    maxCrawlDepth: number;
    crawlTimeoutMs: number;
    guestAnalysesPerDay: number;
    userAnalysesPerDay: number;
    maxImagesPerAnalysis: number;
  };
  cache: { enabled: boolean; ttlSeconds: number };
  version: string;
  viewportSizes: Record<Viewport, { width: number; height: number }>;
  steps: ProgressStep[];
  exportFormats: ExportFormat[];
  scoreFormula?: string;
  session: {
    authenticated: boolean;
    email: string | null;
    subscribed: boolean;
    isAdmin: boolean;
    quota: { used: number; limit: number | null; resetAt: string };
  };
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    detail: string | null;
    retryable: boolean;
    fields?: Array<{ path: string; message: string }>;
  };
}
